/**
 * Metric card downloads: the card's data as Excel plus a picture of the card.
 *
 * Scott, 2026-10-01: downloads should not have to be CSV; a card download should
 * also give a PNG of the card; and several cards can be downloaded at once, each
 * card's data on its own tab of one workbook. Choices he made: single card ->
 * .xlsx + .png; several cards -> one .zip holding the workbook and a PNG per card.
 *
 * The rows are the SAME population as the card on screen: the drill detail fetched
 * with the card's own filters (see the note in MetricCard's export), falling back
 * to the card's displayed values. Single and multi downloads share cardRows() so
 * they cannot drift apart.
 */
import * as XLSX from 'xlsx'
import { toBlob } from 'html-to-image'
import { drillMetric } from '../../api'

// Card chrome that is not part of the picture.
const NOT_IN_PICTURE = ['wt-card-actions', 'wt-card-select', 'wt-result-drill']

function stamp() {
  const d = new Date()
  const p = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

function safeFile(s) {
  return String(s || 'card').replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 80) || 'card'
}

/** Rows for one card: { columns, rows, source: 'detail' | 'card' }. */
export async function cardRows({ customerId, key, value, filters }) {
  try {
    const res = await drillMetric(customerId, key, 10000, null, filters || null)
    if (res && Array.isArray(res.rows) && res.rows.length && Array.isArray(res.columns) && res.columns.length) {
      return { columns: res.columns, rows: res.rows, source: 'detail' }
    }
  } catch (_) { /* fall back to the card's own values */ }
  if (Array.isArray(value) && value.length) {
    const columns = [...new Set(value.flatMap(r => (r && typeof r === 'object') ? Object.keys(r) : []))]
    if (columns.length) return { columns, rows: value, source: 'card' }
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return { columns: Object.keys(value), rows: [value], source: 'card' }
  }
  return { columns: ['metric', 'value'], rows: [{ metric: key, value }], source: 'card' }
}

function rowsToSheet({ columns, rows }) {
  const aoa = [columns, ...rows.map(r => columns.map(c => {
    const v = r?.[c]
    return v === null || v === undefined ? '' : (typeof v === 'object' ? JSON.stringify(v) : v)
  }))]
  const ws = XLSX.utils.aoa_to_sheet(aoa)
  ws['!cols'] = columns.map(c => ({ wch: Math.min(40, Math.max(10, String(c).length + 2)) }))
  return ws
}

/** Excel tab names: max 31 chars, no []:*?/\ , unique within the workbook. */
export function sheetName(label, used) {
  const base = String(label || 'Card').replace(/[[\]:*?/\\]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 31) || 'Card'
  let name = base, n = 2
  while (used.has(name.toLowerCase())) {
    const suffix = ` (${n++})`
    name = base.slice(0, 31 - suffix.length) + suffix
  }
  used.add(name.toLowerCase())
  return name
}

// The site's web fonts, inlined for the picture. html-to-image draws the card through
// an SVG image, which cannot load fonts by itself, so without this the picture falls
// back to a system font. The faces are self-hosted (public/fonts), so their @font-face
// rules are readable from the page's own stylesheets. Only the families the card uses
// are inlined, and each font file is fetched once per page load.
const _fontData = new Map()
function fontDataUrl(href) {
  if (!_fontData.has(href)) {
    _fontData.set(href, fetch(href)
      .then(r => { if (!r.ok) throw new Error(`${r.status} ${href}`); return r.blob() })
      .then(blob => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob) }))
      .catch(err => { _fontData.delete(href); throw err }))
  }
  return _fontData.get(href)
}

const familyName = f => f.trim().replace(/["']/g, '')

async function fontEmbedCSS(el) {
  try {
    const used = new Set()
    for (const n of [el, ...el.querySelectorAll('*')]) {
      getComputedStyle(n).fontFamily.split(',').forEach(f => used.add(familyName(f)))
    }
    const parts = []
    for (const sheet of document.styleSheets) {
      let rules
      try { rules = sheet.cssRules } catch (_) { continue }  // cross-origin sheet: not a self-hosted face
      for (const rule of rules) {
        if (!(rule instanceof CSSFontFaceRule)) continue
        if (!used.has(familyName(rule.style.getPropertyValue('font-family')))) continue
        const m = rule.cssText.match(/url\(\s*["']?([^"')]+)["']?\s*\)/)
        if (!m || m[1].startsWith('data:')) continue
        const dataUrl = await fontDataUrl(new URL(m[1], sheet.href || document.baseURI).href)
        parts.push(rule.cssText.replace(m[0], `url("${dataUrl}")`))
      }
    }
    return parts.join('\n')
  } catch (err) {
    console.error('Font embedding failed:', err)
    return ''
  }
}

/** PNG of the card exactly as it looks on screen, without its buttons. */
export async function cardPicture(el) {
  if (!el) return null
  return toBlob(el, {
    pixelRatio: 2,
    backgroundColor: '#ffffff',
    cacheBust: true,
    fontEmbedCSS: await fontEmbedCSS(el),
    filter: node => !(node.classList && NOT_IN_PICTURE.some(c => node.classList.contains(c))),
  })
}

function save(blob, name) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1500)
}

function workbookBlob(wb) {
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
  return new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}

function filtersText(filters) {
  if (!filters || !Object.keys(filters).length) return 'None'
  return Object.entries(filters).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ')
}

function summaryValue(value) {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return `${value.length} rows (see tab)`
  if (typeof value === 'object') return 'See tab'
  return value
}

/** One card: <customer>_<card>_<date>.xlsx and .png. */
export async function downloadCard({ customerId, metric, filters, el }) {
  const data = await cardRows({ customerId, key: metric.key, value: metric.value, filters })
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, rowsToSheet(data), sheetName(metric.label || metric.key, new Set()))
  const base = `${safeFile(customerId)}_${safeFile(metric.key)}_${stamp()}`
  save(workbookBlob(wb), `${base}.xlsx`)
  try {
    const png = await cardPicture(el)
    if (png) save(png, `${base}.png`)
  } catch (err) {
    console.error('Card picture failed:', err)
  }
}

/**
 * Several cards: one .zip with <customer>_metrics_<date>.xlsx (a Summary tab, then
 * one tab per card) and cards/<n>_<card>.png for each card.
 * items: [{ metric, el, category }]
 */
export async function downloadCards({ customerId, customerName, items, filters }) {
  const JSZip = (await import('jszip')).default
  const zip = new JSZip()
  const wb = XLSX.utils.book_new()
  const used = new Set(['summary'])
  const summary = []
  const datas = await Promise.all(items.map(({ metric }) =>
    cardRows({ customerId, key: metric.key, value: metric.value, filters })))
  items.forEach(({ metric, category }, i) => {
    const tab = sheetName(metric.label || metric.key, used)
    XLSX.utils.book_append_sheet(wb, rowsToSheet(datas[i]), tab)
    summary.push({
      Card: metric.label || metric.key,
      Category: category || '',
      Value: summaryValue(metric.value),
      Tab: tab,
      Rows: datas[i].rows.length,
      'Rows are': datas[i].source === 'detail' ? 'Detail rows (same as View rows)' : 'Card values',
    })
  })
  const head = [
    ['Customer', customerName || customerId],
    ['Filters', filtersText(filters)],
    ['Downloaded', new Date().toLocaleString()],
    [],
  ]
  const ws = XLSX.utils.aoa_to_sheet(head)
  XLSX.utils.sheet_add_json(ws, summary, { origin: -1 })
  ws['!cols'] = [{ wch: 40 }, { wch: 22 }, { wch: 20 }, { wch: 32 }, { wch: 8 }, { wch: 30 }]
  XLSX.utils.book_append_sheet(wb, ws, 'Summary')
  // Summary first.
  wb.SheetNames = ['Summary', ...wb.SheetNames.filter(n => n !== 'Summary')]
  const base = `${safeFile(customerId)}_metrics_${stamp()}`
  zip.file(`${base}.xlsx`, XLSX.write(wb, { bookType: 'xlsx', type: 'array' }))
  for (let i = 0; i < items.length; i++) {
    try {
      const png = await cardPicture(items[i].el)
      if (png) zip.file(`cards/${String(i + 1).padStart(2, '0')}_${safeFile(items[i].metric.key)}.png`, png)
    } catch (err) {
      console.error('Card picture failed:', items[i].metric.key, err)
    }
  }
  save(await zip.generateAsync({ type: 'blob' }), `${base}.zip`)
}
